import { createRxDatabase, CollectionsOfDatabase, addRxPlugin, RxStorage, RxDatabase } from 'rxdb'
import { syncEntities } from '@obeta/data/lib/entities'
import { EntityNames, EntityDescriptor, ObetaModels } from '@obeta/models/lib/models/Db/index'
import { entityMeta } from '@obeta/data/lib/entities/entityMetaData'
import { catchError, defer, from, of, Subject } from 'rxjs'
import { concatMap, mergeMap } from 'rxjs/operators'
import { RxGraphQLReplicationState } from 'rxdb/plugins/replication-graphql'
import { RxDBMigrationPlugin } from 'rxdb/plugins/migration'
import { RxDBLocalDocumentsPlugin } from 'rxdb/plugins/local-documents'
import { RxDBCleanupPlugin } from 'rxdb/plugins/cleanup'
import { RxDBUpdatePlugin } from 'rxdb/plugins/update'
import { changeMetaData } from '@obeta/utils/lib/epics-helpers'
import type { IRecommendations } from '@obeta/data/lib/hooks/useSearchRecommendations'
import type { SearchHistory } from '@obeta/data/lib/hooks/useSearchHistory'
import { bootstrapInitialMetaDataDocs } from '@obeta/utils/lib/epics-helpers'
import { getRxDbStorage } from './getRxDbStorage'
import { resync } from './resync'
import { replicateGraphQL } from 'rxdb/plugins/replication-graphql'
import { RxDBLeaderElectionPlugin } from 'rxdb/plugins/leader-election'
import { Tokens } from '@obeta/models/lib/models/BusinessLayer/AppActions'
import { BroadcastChannel } from 'broadcast-channel'
import { SecureStoragePlugin, WebStorageAdapter } from '@atroo/capacitor-secure-storage-plugin'
import { isPlatform } from '@obeta/utils/lib/isPlatform'

/**
 * make sure that plugins added only once
 */
let pluginsAdded = false

let db: RxDatabase

interface ReplicationAction {
  type: 'start' | 'stop'
  entities?: string[]
  baseUrl?: string
  token?: string
  errorHandler?: (err: Error) => void
}

type TReplicationState = RxGraphQLReplicationState<unknown, unknown>

export const replication$ = new Subject<ReplicationAction>()

// filtering function
const filterEntitiesToActiveApp = (entitiesToUse?: string[]) => {
  let entityList = syncEntities
  if (entitiesToUse && entitiesToUse.length > 0) {
    entityList = entitiesToUse.map((entity) =>
      syncEntities.find((ent) => ent.name === entity)
    ) as typeof syncEntities
  }
  return entityList
}

// Start a sync for an entity
const startSync = async (
  ent: EntityDescriptor<ObetaModels, unknown>,
  action: ReplicationAction
) => {
  const client = await db.getLocal('client')
  const frontendClientId = client?.get('frontendClientId')
  const replState = resync.getReplState(ent.name)

  if (replState && ent.authentication === true && action.token) {
    replState.setHeaders({
      Authorization: `bearer ${action.token}`,
      'x-frontend-client-id': frontendClientId,
    })
    return null
  }

  if (ent.pullSync && action.baseUrl && !replState) {
    // without token, we won't create syncs, that need auth
    // this happens when the user signed in as guest
    if (!action.token && ent.authentication === true) {
      return null
    }

    if (!db[ent.name]) {
      return null
    }
    const nextReplState = replicateGraphQL({
      collection: db[ent.name],
      url: { http: action.baseUrl },
      deletedField: 'deleted', // the flag which indicates if a pulled document is deleted
      pull: ent.pullSync,
      live: true,
      retryTime: 60 * 1000,
      ...(ent.authentication === true &&
        action.token && {
          headers: {
            Authorization: `Bearer ${action.token}`,
            'x-frontend-client-id': frontendClientId,
          },
        }),
      // TODO: Check why typescript not happy in runResyncLoop with ObetaModels instead of any
      /* eslint-disable @typescript-eslint/no-explicit-any */
    }) as RxGraphQLReplicationState<any, unknown>

    resync.runResyncLoop(ent.name, nextReplState, ent.liveInterval || 30 * 1000)

    if (action.errorHandler) {
      nextReplState.error$.subscribe(action.errorHandler)
    }
    return nextReplState
  }
  return null
}

const waitForInitialReplication = async (
  replState: RxGraphQLReplicationState<any, unknown>,
  ent: EntityDescriptor<ObetaModels, unknown>
) => {
  try {
    db.waitForLeadership().then(async () => {
      await changeMetaData(db, ent.name, { isFetching: true })
      await replState.awaitInitialReplication()
      await changeMetaData(db, ent.name, { isFetching: false })
    })
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log('Could not change metadata on leadership change')
    console.error(err)
  }
}

// Pipeline
replication$
  .pipe(
    //run all replication requests one after another
    concatMap((action) =>
      from(filterEntitiesToActiveApp(action.entities)).pipe(
        mergeMap((entity) => {
          //create the replicationStates inParallel or stop them respectivley
          if (action.type === 'stop') {
            resync.cancelReplication(entity.name)

            return of(undefined)
          } else if (action.type === 'start') {
            return defer(async () => {
              const replState = await startSync(entity, action)
              if (!replState) return undefined

              return { replState, entity }
            })
          }
          return of()
        }),
        catchError((e) => {
          //eslint-disable-next-line
          console.log(e)
          return of(undefined)
        })
      )
    ),
    //if we create new repl states we will wait for their initial replication
    mergeMap((replicationStateResult) => {
      if (!replicationStateResult) return of()
      return defer(async () => {
        if (replicationStateResult) {
          await waitForInitialReplication(
            replicationStateResult.replState,
            replicationStateResult.entity
          )
        }
        return
      })
    }),
    catchError((e) => {
      //eslint-disable-next-line
      console.log(e)
      return of(undefined)
    })
  )
  .subscribe()

export const bootstrapDatabase = async (
  multiInstance = true,
  ignoreDuplicate = false,
  entitiesToUse?: EntityNames[],
  forceNewInstance?: boolean,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  forcedStorageAdapter?: () => RxStorage<any, any>
) => {
  if (db) {
    if (forceNewInstance) {
      await db.remove()
      await db.destroy()
    } else {
      return db
    }
  }
  try {
    const storage = getRxDbStorage(forcedStorageAdapter)

    if (!pluginsAdded) {
      addRxPlugin(RxDBMigrationPlugin)
      addRxPlugin(RxDBLocalDocumentsPlugin)
      addRxPlugin(RxDBCleanupPlugin)
      addRxPlugin(RxDBUpdatePlugin)
      addRxPlugin(RxDBLeaderElectionPlugin)
      if (process.env.NODE_ENV === 'development') {
        const module = await import('rxdb/plugins/dev-mode')
        addRxPlugin(module.RxDBDevModePlugin)
      }
      pluginsAdded = true
    }

    db = await createRxDatabase<CollectionsOfDatabase>({
      name: 'obetav14', // <- name
      storage,
      eventReduce: true, // <- queryChangeDetection (optional, default: false)
      multiInstance, // <- multiInstance (optional, default: true)
      ignoreDuplicate,
      localDocuments: true,
      cleanupPolicy: {
        minimumDeletedTime: 1000 * 60 * 60 * 24 * 7, // week
        minimumCollectionAge: 1000 * 60, // 1 min
        runEach: 1000 * 60 * 5, // 5 minutes
        awaitReplicationsInSync: true,
        waitForLeadership: true,
      },
    })

    try {
      const userMetaDoc = await db.getLocal('usermeta')
      if (!userMetaDoc) {
        await db.upsertLocal('usermeta', {})
      }

      const userDoc2 = await db.getLocal('userv2')
      if (!userDoc2) {
        await db.upsertLocal('userv2', {})
      }

      const searchRecommendations = await db.getLocal<IRecommendations>('recommendations')
      if (!searchRecommendations) {
        await db.upsertLocal<IRecommendations>('recommendations', { data: [] })
      }

      const searchHistoryDoc = await db.getLocal<SearchHistory>('searchHistory')
      if (!searchHistoryDoc) {
        await db.upsertLocal('searchHistory', {
          history: [],
        })
      }

      const searchSuggestions = await db.getLocal('searchSuggestions')
      if (!searchSuggestions) {
        await db.upsertLocal('searchSuggestions', {
          suggestions: [],
        })
      }

      const pushToken = await db.getLocal('pushToken')
      if (!pushToken) {
        await db.upsertLocal('pushToken', { token: null })
      }

      const openPositions = await db.getLocal('openpositions')
      if (!openPositions) {
        await db.upsertLocal('openpositions', [])
      }

      const client = await db.getLocal('client')
      if (!client) {
        const frontendClientId = Math.random().toString(36).substring(2, 11)
        await db.upsertLocal('client', { frontendClientId })
      }
    } catch (e) {
      //this is a writeConflict, meaning that most likley a parallel tab is also currently creating this element
      const isWriteConflict = e.status === 409
      if (!isWriteConflict) {
        throw e
      }
    }

    //create entitymeta collection first
    await db.addCollections({
      [entityMeta.name]: {
        schema: entityMeta.schema,
        migrationStrategies: entityMeta.migrationStrategies,
        localDocuments: true,
      },
    })

    let entitiesToManage: EntityDescriptor[] = syncEntities
    if (entitiesToUse) {
      entitiesToManage = syncEntities.filter(
        (ent: EntityDescriptor) => entitiesToUse.indexOf(ent.name) !== -1 // todo: why not to use entitiesToUse instead of filtering?
      )
    }

    const data: Record<
      string,
      Pick<EntityDescriptor, 'schema' | 'migrationStrategies' | 'localDocuments'>
    > = {}
    entitiesToManage.forEach((ent: EntityDescriptor) => {
      data[ent.name] = {
        schema: ent.schema,
        migrationStrategies: ent.migrationStrategies,
        localDocuments: true,
      }
    })
    await db.addCollections(data)

    const entityNames: EntityNames[] = []
    entitiesToManage.forEach((ent: EntityDescriptor) => {
      entityNames.push(ent.name)
    })

    if (db.isLeader()) {
      await bootstrapInitialMetaDataDocs(db, entityNames)
    } else {
      db.waitForLeadership().then(() => {
        return bootstrapInitialMetaDataDocs(db, entityNames)
      })
    }

    return db
  } catch (e) {
    console.error(e)
    throw new Error(e.message)
  }
}

export const getCollectionSync = (entityName: EntityNames): TReplicationState | undefined =>
  resync.getReplState(entityName)

export const syncDataWithDatabase = async (
  collectionName: EntityNames,
  requestData: Record<string, unknown>[],
  compareKey: string
) => {
  const collection = db[collectionName]
  const prevData: object[] = (await collection?.find().exec()) ?? []

  const toRemove: string[] = []

  prevData.forEach((prev) => {
    const exists = requestData.find((store) => store[compareKey] === prev[compareKey])
    if (!exists) toRemove.push(prev[compareKey])
  })

  await collection.bulkUpsert(requestData)

  if (toRemove.length > 0) await collection.bulkRemove(toRemove)
}

export const cancelReplication = resync.cancelReplications

interface MessageInitialTokenReceived {
  action: 'token_received' | 'token_requested'
  tokens?: Tokens
}

export const sessionStorageInitialTokenChannel = new BroadcastChannel<MessageInitialTokenReceived>(
  'session_storage_initial_token',
  {
    type: typeof window === 'undefined' ? 'simulate' : 'native',
  }
)

export const bootstrapTokens = async () => {
  // First try to get auth from secure storage
  try {
    try {
      const { value } = await SecureStoragePlugin.get({ key: 'auth' })
      if (value) {
        return JSON.parse(value)
      }
    } catch (e) {
      //if none is set, the get will error
    }

    // No auth found, check if we should use broadcast channel to see if there might be other "private mode tabs that may have login information"
    if (isPlatform('web')) {
      // will be used for shop mainly, app uses this bootstrap function too though, as it also can be run in web e.g. dev or app-debug
      // if we have configured the session storage for short lived sessions, we need to
      // request the tokens from another tab, that actually has them
      // the reason is, that the boundary of sessionStorage - unlike session cookies - is the current tab
      // not the browser lifespan

      const { adapter } = await SecureStoragePlugin.getStorageAdapter()
      if (adapter === WebStorageAdapter.SessionStorage) {
        // Try to get token from other tabs
        try {
          const tokenFromOtherTab = await new Promise<Tokens | null>((resolve) => {
            // Set timeout
            const timeoutId = setTimeout(() => {
              resolve(null)
            }, 500)

            // Listen for responses
            sessionStorageInitialTokenChannel.onmessage = async (data) => {
              if (data.action === 'token_received' && data.tokens) {
                clearTimeout(timeoutId)
                await SecureStoragePlugin.set({
                  key: 'auth',
                  value: JSON.stringify(data.tokens),
                  accessibility: 'afterFirstUnlock',
                })
                resolve(data.tokens)
              }
            }

            // Request tokens
            sessionStorageInitialTokenChannel.postMessage({
              action: 'token_requested',
            })
          })

          return tokenFromOtherTab
        } catch (err) {
          console.error('Error requesting tokens from other tabs:', err)
        } finally {
          //remove this message handler, we will only ever listen for token recieves while loading
          sessionStorageInitialTokenChannel.onmessage = null
        }
      }
    }
  } catch (err) {
    console.error('Error getting auth:', err)
  }

  // Return null if all methods fail
  return null
}
